import React, { useState, useEffect } from 'react'
import {
    makeStyles,
    Theme,
    Card,
    CardHeader,
    CardContent,
    Typography,
    Avatar,
    TextField,
    InputAdornment,
    IconButton,
} from '@material-ui/core'
import Link from '@material-ui/core/Link'
import CircularProgress from '@material-ui/core/CircularProgress'
import { green } from '@material-ui/core/colors'
import OfflineBoltIcon from '@material-ui/icons/OfflineBolt'
import Clear from '@material-ui/icons/Clear'
import Spinner from 'common/spinner'
import ContractClient from 'clients/contract-client'
import { Audit, filterAudits } from '../../models/audit'
import { formatString } from 'utils/general-utils'
import { toDateTimeFormat } from 'utils/time/timeUtil'
import { tr } from 'utils/translations/translate'
import {
    TEXT_AT,
    TEXT_INVOICES_WERE_CREATED,
    TEXT_INVOICES_WERE_PUBLISHED,
    TEXT_PRODUCT_AUDIT_ADDED,
    TEXT_PRODUCT_AUDIT_REMOVED,
    TEXT_PRODUCT_AUDIT_UPDATED,
    TEXT_SEARCH,
    TEXT_SEARCHING,
    TEXT_SHOW_MORE,
    TEXT_HOUSING_UNIT_AUDIT_ADDED,
    TEXT_HOUSING_UNIT_AUDIT_UPDATED,
    TEXT_HOUSING_UNIT_AUDIT_REMOVED,
    TEXT_CONTRACT_AUDIT_UPDATED,
    TEXT_UNDEFINED,
} from 'utils/translations/keys'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        minWidth: 275,
        marginBottom: '10px',
    },
    title: {
        fontSize: 14,
        fontWeight: 500,
    },
    text: {
        fontSize: 12,
        fontWeight: 200,
        width: '240px',
    },
    avatar: {
        backgroundColor: green[500],
    },
    cardContent: {
        padding: '0 10px 0 10px',
        paddingBottom: 0,
    },
    searchField: {
        marginBottom: theme.spacing(1),
    },
    searchStatus: {
        minHeight: theme.spacing(2),
        marginBottom: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    showMoreContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    showMoreSpinner: {
        marginLeft: theme.spacing(1),
    },
}))

type ContractAuditProps = {
    contractId: string
}

const ContractAudit: React.FC<ContractAuditProps> = ({ contractId }) => {
    const styles = useStyles()
    const pageSize = 5
    const searchDebounceMs = 300

    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState<Audit[]>([])
    const [searchText, setSearchText] = useState('')
    const [debouncedSearchText, setDebouncedSearchText] = useState('')
    const [visibleCount, setVisibleCount] = useState(pageSize)
    const [isLoadingMore, setIsLoadingMore] = useState(false)

    useEffect(() => {
        const getData = async () => {
            setIsLoading(true)
            const result = await ContractClient.getContractAudit(contractId).toPromise()
            setData(result.items)
            setVisibleCount(pageSize)
            setIsLoading(false)
        }

        getData()
    }, [contractId])

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearchText(searchText)
        }, searchDebounceMs)

        return () => window.clearTimeout(timeoutId)
    }, [searchText])

    if (isLoading) {
        return (
            <Card className={styles.root} variant="outlined">
                <Spinner />
            </Card>
        )
    }

    enum InvoiceAuditType {
        InvoicePublished = 'InvoicePublished',
        InvoiceCreated = 'InvoiceCreated',
    }

    enum AuditAction {
        Insert = 'Insert',
        Update = 'Update',
        Delete = 'Delete',
    }

    enum EntityType {
        Invoice = 'Invoice',
        Product = 'Product',
        HousingUnit = 'HousingUnit',
        Contract = 'Contract',
    }

    const isSearching = searchText !== debouncedSearchText
    const invoicesWereCreatedText = tr(TEXT_INVOICES_WERE_CREATED)
    const invoicesWerePublishedText = tr(TEXT_INVOICES_WERE_PUBLISHED)
    const getProductAddedText = (entityName: string) => formatString(tr(TEXT_PRODUCT_AUDIT_ADDED), entityName)
    const getProductUpdatedText = (entityName: string) => formatString(tr(TEXT_PRODUCT_AUDIT_UPDATED), entityName)
    const getProductRemovedText = (entityName: string) => formatString(tr(TEXT_PRODUCT_AUDIT_REMOVED), entityName)
    const getHousingUnitAddedText = (entityName: string) => formatString(tr(TEXT_HOUSING_UNIT_AUDIT_ADDED), entityName)
    const getHousingUnitUpdatedText = (entityName: string) => formatString(tr(TEXT_HOUSING_UNIT_AUDIT_UPDATED), entityName)
    const getHousingUnitRemovedText = (entityName: string) => formatString(tr(TEXT_HOUSING_UNIT_AUDIT_REMOVED), entityName)
    const getContractUpdatedText = (entityName: string) => formatString(tr(TEXT_CONTRACT_AUDIT_UPDATED), entityName)
    const translateAuditValue = (value: string) => (value === undefined ? tr(TEXT_UNDEFINED) : tr(value) || value)
    const filteredData = filterAudits(data, debouncedSearchText, (audit) => {

        if (audit.entityType === EntityType.Invoice && audit.fields.some((field) => field.fieldName === InvoiceAuditType.InvoicePublished)) {
            return [invoicesWerePublishedText]
        }

        if (audit.entityType === EntityType.Invoice && audit.fields.some((field) => field.fieldName === InvoiceAuditType.InvoiceCreated)) {
            return [invoicesWereCreatedText]
        }

        if (audit.entityType === EntityType.Product && audit.fields.some((field) => field.action === AuditAction.Insert)) {
            return [getProductAddedText(audit.entityName)]
        }

        if (audit.entityType === EntityType.Product && audit.fields.some((field) => field.action === AuditAction.Update)) {
            return [getProductUpdatedText(audit.entityName)]
        }

        if (audit.entityType === EntityType.Product && audit.fields.some((field) => field.action === AuditAction.Delete)) {
            return [getProductRemovedText(audit.entityName)]
        }

        if (audit.entityType === EntityType.HousingUnit && audit.fields.some((field) => field.action === AuditAction.Insert)) {
            return [getHousingUnitAddedText(audit.entityName)]
        }

        if (audit.entityType === EntityType.HousingUnit && audit.fields.some((field) => field.action === AuditAction.Update)) {
            return [getHousingUnitUpdatedText(audit.entityName)]
        }

        if (audit.entityType === EntityType.HousingUnit && audit.fields.some((field) => field.action === AuditAction.Delete)) {
            return [getHousingUnitRemovedText(audit.entityName)]
        }

        if (audit.entityType === EntityType.Contract && audit.fields.some((field) => field.action === AuditAction.Update)) {
            return [getContractUpdatedText(audit.entityName)]
        }

        return []
    })
    const pagedData = filteredData.slice(0, visibleCount)
    const hasMoreData = visibleCount < filteredData.length

    const handleSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value)
        setVisibleCount(pageSize)
    }

    const handleClearSearch = () => {
        setSearchText('')
        setVisibleCount(pageSize)
    }

    const handleShowMore = async () => {
        if (isLoadingMore) {
            return
        }

        setIsLoadingMore(true)
        await new Promise((resolve) => setTimeout(resolve, 250))
        setVisibleCount((prev) => Math.min(prev + pageSize, filteredData.length))
        setIsLoadingMore(false)
    }



    return (
        <>
            <TextField
                value={searchText}
                onChange={handleSearchChanged}
                label={tr(TEXT_SEARCH)}
                margin="normal"
                variant="outlined"
                fullWidth
                className={styles.searchField}
                InputProps={{
                    endAdornment: searchText && (
                        <InputAdornment position="end">
                            <IconButton edge="end" onClick={handleClearSearch}>
                                <Clear fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <Typography variant="caption" className={styles.searchStatus}>
                {isSearching ? tr(TEXT_SEARCHING) : ' '}
            </Typography>
            {
                pagedData.map((item, index) => {

                    const added = item.fields.some((field) => field.action === AuditAction.Insert)
                    const updated = item.fields.some((field) => field.action === AuditAction.Update)
                    const deleted = item.fields.some((field) => field.action === AuditAction.Delete)
                    const title = !!item.userName ? item.userName : 'Unknown'

                    return (
                        <Card className={styles.root} variant="outlined" key={index}>
                            <CardHeader
                                avatar={
                                    <Avatar aria-label="recipe" className={styles.avatar}>
                                        <OfflineBoltIcon />
                                    </Avatar>
                                }
                                title={title}
                                subheader={toDateTimeFormat(item.auditDate, tr(TEXT_AT))}
                            />
                            <CardContent className={styles.cardContent}>
                                {item.entityType === EntityType.Product && (
                                    <Typography className={styles.title}>
                                        {added && getProductAddedText(item.entityName)}
                                        {updated && getProductUpdatedText(item.entityName)}
                                        {deleted && getProductRemovedText(item.entityName)}
                                    </Typography>
                                )}

                                {item.entityType === EntityType.HousingUnit && (
                                    <Typography className={styles.title}>
                                        {added && getHousingUnitAddedText(item.entityName)}
                                        {updated && getHousingUnitUpdatedText(item.entityName)}
                                        {deleted && getHousingUnitRemovedText(item.entityName)}
                                    </Typography>
                                )}

                                {item.entityType === EntityType.Contract && (
                                    <Typography className={styles.title}>
                                        {updated && getContractUpdatedText(item.entityName)}
                                    </Typography>
                                )}

                                {(item.entityType === EntityType.Invoice && item.fields.some((field) => field.fieldName === InvoiceAuditType.InvoicePublished))
                                    && (
                                        <div key={index}>
                                            <Typography className={styles.title}>{invoicesWerePublishedText}</Typography>
                                        </div>
                                    )}


                                {(item.entityType === EntityType.Invoice && item.fields.some((field) => field.fieldName === InvoiceAuditType.InvoiceCreated))
                                    && (
                                        <div key={index}>
                                            <Typography className={styles.title}>{invoicesWereCreatedText}</Typography>
                                        </div>
                                    )}

                                {item.fields.map((field: any, index: number) => {
                                    if ((item.entityType === EntityType.Product && field.action === AuditAction.Update) ||
                                        (item.entityType === EntityType.Contract && (field.action === AuditAction.Insert || field.action === AuditAction.Update))) {
                                        return (
                                            <div key={index}>
                                                <Typography className={styles.text}>
                                                    {tr(field.fieldName) || field.fieldName}
                                                    {': '}
                                                    {`${translateAuditValue(field.oldValue)} -> ${translateAuditValue(field.newValue)}`}
                                                </Typography>
                                            </div>
                                        )
                                    }

                                    return null
                                })}
                            </CardContent>
                        </Card>
                    )
                })}
            {hasMoreData && (
                <div className={styles.showMoreContainer}>
                    <Link component="button" variant="body2" onClick={handleShowMore}>
                        {tr(TEXT_SHOW_MORE)}
                    </Link>
                    {isLoadingMore && <CircularProgress size={14} className={styles.showMoreSpinner} />}
                </div>
            )}
        </>
    )
}

export { ContractAudit }
